import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { useMsal } from '@azure/msal-react';
import { useAuth } from './AuthContext';
import { authAPI } from '../services/api';

const Callback: React.FC = () => {
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const { setAuthFromStorage } = useAuth();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        setLoading(true);

        // If redirect from login Microsoft
        if (accounts.length > 0) {
          // Get the most recently authenticated account (usually the last one in the array)
          // This ensures we use the account that was just selected, not an old cached one
          const account = accounts[accounts.length - 1];

          console.log(
            '[Callback] Available accounts:',
            accounts.map((a: any) => a.username)
          );
          console.log('[Callback] Using account:', account.username);

          instance.setActiveAccount(account);

          const tokenResponse = await instance.acquireTokenSilent({
            scopes: ['User.Read'],
            account,
          });

          // Send token to backend for verification and JWT issuance using centralized API service
          const response = await authAPI.microsoftLogin({
            accessToken: tokenResponse.accessToken,
          });

          if (!response.data || !response.data.token || !response.data.user) {
            throw new Error('Invalid response from server: missing token or user data');
          }

          const data = response.data;

          // Store JWT and user info
          localStorage.setItem('authToken', data.token);
          localStorage.setItem('user', JSON.stringify(data.user));
          setAuthFromStorage();

          navigate('/dashboard', { replace: true });
        } else {
          setError('Microsoft authentication failed. Please try again.');
          setTimeout(() => navigate('/login', { replace: true }), 3000);
        }
      } catch (err: any) {
        console.error('Callback error:', err);
        const errorMsg = err.message || 'Authentication failed';
        setError(errorMsg);
        // Give more time for user-not-found errors so they can read the message
        const delay = errorMsg.includes('not registered') ? 5000 : 3000;
        setTimeout(() => navigate('/login', { replace: true }), delay);
      } finally {
        setLoading(false);
      }
    };

    handleCallback();
  }, [instance, accounts, navigate, setAuthFromStorage]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
    >
      {loading && !error && (
        <>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography>Processing authentication...</Typography>
        </>
      )}
      {error && (
        <>
          <Alert severity="error" sx={{ mb: 2, maxWidth: 400 }}>
            {error}
          </Alert>
          <Typography variant="body2" color="textSecondary">
            Redirecting to login...
          </Typography>
        </>
      )}
    </Box>
  );
};

export default Callback;
